import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Home, Plus, Pencil, Trash2, Search, AlertTriangle, Phone, Wallet, Calendar, RefreshCw, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const RentalsPage = () => {
  const { token, isAdmin } = useAuth();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenewDialogOpen, setIsRenewDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedRental, setSelectedRental] = useState(null);
  const [renewField, setRenewField] = useState('');
  const [renewDate, setRenewDate] = useState('');
  const [formData, setFormData] = useState({
    property_name: '',
    tenant_name: '',
    tenant_phone: '',
    rental_amount: '',
    payment_frequency: 'monthly',
    contract_start: '',
    contract_end: '',
    next_payment_date: '',
    property_id: '',
    notes: ''
  });

  const fetchRentals = async () => {
    try {
      const response = await axios.get(`${API}/rentals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRentals(response.data);
    } catch (error) {
      toast.error('خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRentals();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData, rental_amount: parseFloat(formData.rental_amount) };
      if (selectedRental) {
        await axios.put(`${API}/rentals/${selectedRental.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('تم تحديث الإيجار بنجاح');
      } else {
        await axios.post(`${API}/rentals`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('تم إضافة الإيجار بنجاح');
      }
      setIsDialogOpen(false);
      fetchRentals();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/rentals/${selectedRental.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حذف الإيجار بنجاح');
      setIsDeleteDialogOpen(false);
      fetchRentals();
    } catch (error) {
      toast.error('خطأ في حذف الإيجار');
    }
  };

  const openRenewDialog = (rental, field) => {
    setSelectedRental(rental);
    setRenewField(field);
    setRenewDate('');
    setIsRenewDialogOpen(true);
  };

  const handleRenew = async () => {
    if (!renewDate) {
      toast.error('يرجى إدخال التاريخ الجديد');
      return;
    }
    try {
      const updateData = { ...selectedRental, [renewField]: renewDate };
      await axios.put(`${API}/rentals/${selectedRental.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم التجديد بنجاح');
      setIsRenewDialogOpen(false);
      fetchRentals();
    } catch (error) {
      toast.error('خطأ في التجديد');
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      contract_end: 'العقد',
      next_payment_date: 'موعد الدفع'
    };
    return labels[field] || field;
  };

  const resetForm = () => {
    setFormData({
      property_name: '',
      tenant_name: '',
      tenant_phone: '',
      rental_amount: '',
      payment_frequency: 'monthly',
      contract_start: '',
      contract_end: '',
      next_payment_date: '',
      property_id: '',
      notes: ''
    });
    setSelectedRental(null);
  };

  const openEditDialog = (rental) => {
    setSelectedRental(rental);
    setFormData({
      property_name: rental.property_name,
      tenant_name: rental.tenant_name,
      tenant_phone: rental.tenant_phone || '',
      rental_amount: rental.rental_amount.toString(),
      payment_frequency: rental.payment_frequency,
      contract_start: rental.contract_start,
      contract_end: rental.contract_end,
      next_payment_date: rental.next_payment_date,
      property_id: rental.property_id || '',
      notes: rental.notes || ''
    });
    setIsDialogOpen(true);
  };

  const openViewDialog = (rental) => {
    setSelectedRental(rental);
    setIsViewDialogOpen(true);
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const expiry = new Date(dateStr);
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diff <= 30 && diff >= 0;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const expiry = new Date(dateStr);
    return expiry < today;
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      monthly: 'شهري',
      quarterly: 'ربع سنوي',
      semi_annual: 'نصف سنوي',
      annual: 'سنوي'
    };
    return labels[freq] || freq;
  };

  const filteredRentals = rentals.filter(rental =>
    (rental.property_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (rental.tenant_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rentals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">الإيجارات</h1>
          <p className="text-slate-500 mt-1">إدارة عقود الإيجار والمستأجرين</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="text-white" style={{ background: '#1a365d' }}
            data-testid="add-rental-btn"
          >
            <Plus className="w-4 h-4 ml-2" />
            إضافة إيجار
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="البحث عن إيجار..."
          className="pr-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-rentals"
        />
      </div>

      {/* Rentals Table */}
      <Card className="border-0 shadow-sm" data-testid="rentals-table-card">
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {isAdmin && <TableHead className="text-center w-24">إجراءات</TableHead>}
                  <TableHead className="text-center">استعراض</TableHead>
                  <TableHead className="text-center">موعد الدفع</TableHead>
                  <TableHead className="text-center">انتهاء العقد</TableHead>
                  <TableHead className="text-center">قيمة الإيجار</TableHead>
                  <TableHead className="text-center">المستأجر</TableHead>
                  <TableHead className="text-right">العقار</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRentals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-slate-400">
                      <Home className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>لا توجد إيجارات</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRentals.map((rental) => (
                    <TableRow key={rental.id} className="table-row-hover" data-testid={`rental-row-${rental.id}`}>
                      {isAdmin && (
                        <TableCell className="text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(rental)}
                              data-testid={`edit-rental-${rental.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => { setSelectedRental(rental); setIsDeleteDialogOpen(true); }}
                              data-testid={`delete-rental-${rental.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-blue-600 hover:text-blue-700"
                          onClick={() => openViewDialog(rental)}
                          data-testid={`view-rental-${rental.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-2 justify-center flex-wrap">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="number text-sm">{rental.next_payment_date}</span>
                          {isExpired(rental.next_payment_date) && (
                            <>
                              <Badge variant="destructive" className="text-xs">متأخر</Badge>
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                  onClick={() => openRenewDialog(rental, 'next_payment_date')}
                                >
                                  <RefreshCw className="w-3 h-3 ml-1" />
                                  تجديد
                                </Button>
                              )}
                            </>
                          )}
                          {isExpiringSoon(rental.next_payment_date) && !isExpired(rental.next_payment_date) && (
                            <Badge className="bg-amber-500 text-xs">قريب</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-2 justify-center flex-wrap">
                          <span className="number text-sm">{rental.contract_end}</span>
                          {isExpired(rental.contract_end) && (
                            <>
                              <Badge variant="destructive" className="text-xs">منتهي</Badge>
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                  onClick={() => openRenewDialog(rental, 'contract_end')}
                                >
                                  <RefreshCw className="w-3 h-3 ml-1" />
                                  تجديد
                                </Button>
                              )}
                            </>
                          )}
                          {isExpiringSoon(rental.contract_end) && !isExpired(rental.contract_end) && (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Wallet className="w-4 h-4 text-slate-400" />
                          <span className="number font-medium">{rental.rental_amount.toLocaleString()}</span>
                          <span className="text-xs text-slate-400">ر.س</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div>
                          <p className="font-medium text-slate-900">{rental.tenant_name}</p>
                          {rental.tenant_phone && (
                            <p className="text-sm text-slate-500 flex items-center gap-1 justify-center">
                              <Phone className="w-3 h-3" />
                              {rental.tenant_phone}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-3 justify-end">
                          <div className="text-right">
                            <p className="font-medium text-slate-900">{rental.property_name}</p>
                            <p className="text-xs text-slate-500">{getFrequencyLabel(rental.payment_frequency)}</p>
                          </div>
                          <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                            <Home className="w-5 h-5 text-rose-600" />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle>{selectedRental ? 'تعديل الإيجار' : 'إضافة إيجار جديد'}</DialogTitle>
            <DialogDescription>أدخل بيانات عقد الإيجار</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>اسم العقار</Label>
                <Input
                  value={formData.property_name}
                  onChange={(e) => setFormData({ ...formData, property_name: e.target.value })}
                  placeholder="فيلا الرياض، شقة جدة..."
                  required
                  data-testid="rental-property-input"
                />
              </div>
              <div className="space-y-2">
                <Label>اسم المستأجر</Label>
                <Input
                  value={formData.tenant_name}
                  onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
                  required
                  data-testid="rental-tenant-input"
                />
              </div>
              <div className="space-y-2">
                <Label>هاتف المستأجر</Label>
                <Input
                  value={formData.tenant_phone}
                  onChange={(e) => setFormData({ ...formData, tenant_phone: e.target.value })}
                  data-testid="rental-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>قيمة الإيجار (ر.س)</Label>
                <Input
                  type="number"
                  value={formData.rental_amount}
                  onChange={(e) => setFormData({ ...formData, rental_amount: e.target.value })}
                  required
                  data-testid="rental-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>دورية الدفع</Label>
                <Select
                  value={formData.payment_frequency}
                  onValueChange={(value) => setFormData({ ...formData, payment_frequency: value })}
                >
                  <SelectTrigger data-testid="rental-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">شهري</SelectItem>
                    <SelectItem value="quarterly">ربع سنوي</SelectItem>
                    <SelectItem value="semi_annual">نصف سنوي</SelectItem>
                    <SelectItem value="annual">سنوي</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>تاريخ بداية العقد</Label>
                <Input
                  type="date"
                  value={formData.contract_start}
                  onChange={(e) => setFormData({ ...formData, contract_start: e.target.value })}
                  required
                  data-testid="rental-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label>تاريخ انتهاء العقد</Label>
                <Input
                  type="date"
                  value={formData.contract_end}
                  onChange={(e) => setFormData({ ...formData, contract_end: e.target.value })}
                  required
                  data-testid="rental-end-input"
                />
              </div>
              <div className="space-y-2">
                <Label>تاريخ الدفع القادم</Label>
                <Input
                  type="date"
                  value={formData.next_payment_date}
                  onChange={(e) => setFormData({ ...formData, next_payment_date: e.target.value })}
                  required
                  data-testid="rental-payment-date-input"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>ملاحظات</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="ملاحظات إضافية..."
                  data-testid="rental-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                إلغاء
              </Button>
              <Button type="submit" className="text-white" style={{ background: '#1a365d' }} data-testid="save-rental-btn">
                {selectedRental ? 'حفظ التعديلات' : 'إضافة الإيجار'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader className="text-right">
            <AlertDialogTitle className="text-right">تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              هل أنت متأكد من حذف هذا الإيجار؟ لا يمكن التراجع عن هذا الإجراء.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-rental"
            >
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Renew Dialog */}
      <Dialog open={isRenewDialogOpen} onOpenChange={setIsRenewDialogOpen}>
        <DialogContent className="max-w-sm" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" style={{ color: '#D4AF37' }} />
              تجديد {getFieldLabel(renewField)}
            </DialogTitle>
            <DialogDescription>
              أدخل التاريخ الجديد لـ{getFieldLabel(renewField)}
            </DialogDescription>
          </DialogHeader>
          {selectedRental && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">الإيجار</p>
                <p className="font-medium" style={{ color: '#1a365d' }}>
                  {selectedRental.property_name} - {selectedRental.tenant_name}
                </p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <p className="text-sm text-slate-500">التاريخ الحالي (منتهي/متأخر)</p>
                <p className="font-medium text-red-600 number">{selectedRental[renewField]}</p>
              </div>
              <div className="space-y-2">
                <Label>التاريخ الجديد</Label>
                <Input
                  type="date"
                  value={renewDate}
                  onChange={(e) => setRenewDate(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsRenewDialogOpen(false)}>
              إلغاء
            </Button>
            <Button 
              onClick={handleRenew}
              className="text-white bg-emerald-600 hover:bg-emerald-700"
            >
              <RefreshCw className="w-4 h-4 ml-2" />
              تأكيد التجديد
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Rental Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="w-5 h-5" style={{ color: '#EF4444' }} />
              تفاصيل الإيجار
            </DialogTitle>
          </DialogHeader>
          {selectedRental && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">العقار</p>
                  <p className="font-medium" style={{ color: '#1a365d' }}>{selectedRental.property_name}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">المستأجر</p>
                  <p className="font-medium">{selectedRental.tenant_name}</p>
                </div>
                {selectedRental.tenant_phone && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">هاتف المستأجر</p>
                    <p className="font-medium number">{selectedRental.tenant_phone}</p>
                  </div>
                )}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">قيمة الإيجار</p>
                  <p className="font-medium number">{selectedRental.rental_amount?.toLocaleString()} ر.س</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">دورية الدفع</p>
                  <p className="font-medium">{getFrequencyLabel(selectedRental.payment_frequency)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">بداية العقد</p>
                  <p className="font-medium number">{selectedRental.contract_start}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">نهاية العقد</p>
                  <p className="font-medium number">{selectedRental.contract_end}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">موعد الدفع القادم</p>
                  <p className="font-medium number">{selectedRental.next_payment_date}</p>
                </div>
                {selectedRental.notes && (
                  <div className="p-3 bg-slate-50 rounded-lg col-span-2">
                    <p className="text-sm text-slate-500">ملاحظات</p>
                    <p className="font-medium">{selectedRental.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RentalsPage;
